/// Outer pixel bezel around the whole app (game + subpages).
/// Port of mobile `AppPixelFrame` — 12px margin, radius 9 @ pixelScale 2.

import { useEffect, useRef, type ReactNode } from 'react';

import { appContentMaxWidth } from '@/app/ui/appContentWidth';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { paintPixelBorder } from '@/ui/pixel/pixelBorderPainter';

const frameMargin = 12;
const frameRadius = 9;
const framePixelScale = 2;

export function AppPixelFrame({ children }: { children: ReactNode }) {
  const colors = useThemeColors();
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;

    const repaint = (): void => {
      const { width, height } = host.getBoundingClientRect();
      if (width < 1 || height < 1) return;
      const w = Math.round(width);
      const h = Math.round(height);
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      paintPixelBorder(ctx, w, h, {
        radius: frameRadius,
        pixelScale: framePixelScale,
        borderColor: colors.onSurface,
        fillColor: colors.surface,
      });
    };

    repaint();
    const observer = new ResizeObserver(repaint);
    observer.observe(host);
    return () => observer.disconnect();
  }, [colors.onSurface, colors.surface]);

  // Sit content inside the 1-cell border stroke (matches Flutter insetCells: 1).
  const inset = framePixelScale;

  return (
    <div
      className="box-border h-full w-full"
      style={{
        backgroundColor: colors.surface,
        paddingTop: `max(${frameMargin}px, env(safe-area-inset-top, 0px))`,
        paddingBottom: `max(${frameMargin}px, env(safe-area-inset-bottom, 0px))`,
        paddingLeft: `max(${frameMargin}px, env(safe-area-inset-left, 0px))`,
        paddingRight: `max(${frameMargin}px, env(safe-area-inset-right, 0px))`,
      }}
    >
      <div
        ref={hostRef}
        className="relative mx-auto h-full w-full overflow-hidden"
        style={{ maxWidth: appContentMaxWidth }}
      >
        <canvas
          ref={canvasRef}
          className="pointer-events-none absolute inset-0 h-full w-full"
          aria-hidden
        />
        <div
          className="absolute overflow-hidden"
          style={{
            top: inset,
            right: inset,
            bottom: inset,
            left: inset,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
